import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'

import { getConfigValue, getImmediateTerminationPreview } from 'api'
import ModalForm from 'app/components/ModalForm'
import constants from 'app/constants'
import { eventAlert } from 'app/helpers'

const useStyles = makeStyles((theme) => ({
  formControl: {
    marginBottom: theme.spacing(2),
    width: '100%'
  },
  dangerBox: {
    backgroundColor: '#fdecea',
    border: `1px solid ${theme.palette.error.main}`,
    borderRadius: '4px',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  infoBox: {
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  dangerHeading: {
    fontSize: '14px',
    fontWeight: 600,
    color: theme.palette.error.main,
    marginBottom: theme.spacing(1)
  },
  infoRow: {
    fontSize: '14px',
    marginBottom: theme.spacing(0.5)
  },
  programRow: {
    fontSize: '13px',
    marginLeft: theme.spacing(1)
  }
}))

// Ops must type this before the button arms. Immediate termination is
// irreversible and reaches past the row that was clicked, so a stray double
// click must not be enough to fire it.
const CONFIRM_PHRASE = 'TERMINATE'

// Super-admin-only immediate termination for an Excel enrolment. Unlike the
// normal Terminate (whole month, next month onward), this cuts the student off
// today: sessions later today are cancelled, sessions already run are kept, and
// no refund is issued — the money is settled manually.
const TerminateImmediatelyForm = ({
  isModalOpen,
  formData,
  onSaveClick,
  closeModal
}) => {
  const classes = useStyles()
  const [preview, setPreview] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [reasons, setReasons] = useState([])
  const [reason, setReason] = useState('')
  const [notes, setNotes] = useState('')
  const [confirmText, setConfirmText] = useState('')
  const hasFetchedRef = useRef(false)

  useEffect(() => {
    ;(async () => {
      try {
        const configReasons = await getConfigValue({
          key: 'ALFRED_TERMINATION_REASONS',
          app_name: 'alfred'
        })
        if (Array.isArray(configReasons)) {
          setReasons([...configReasons, 'Other'])
        }
      } catch (error) {
        console.error(error)
      }
    })()
  }, [])

  useEffect(() => {
    if (!isModalOpen || !formData?.uuid || hasFetchedRef.current) return
    hasFetchedRef.current = true
    ;(async () => {
      try {
        setIsLoading(true)
        const response = await getImmediateTerminationPreview({
          enrolment_uuid: formData.uuid
        })
        setPreview(response?.data)
      } catch (error) {
        eventAlert(
          error?.message || 'Unable to load termination preview.',
          constants.ERROR
        )
        closeModal()
      } finally {
        setIsLoading(false)
      }
    })()
  }, [isModalOpen, formData?.uuid])

  useEffect(() => {
    if (!isModalOpen) {
      hasFetchedRef.current = false
      setPreview(null)
      setReason('')
      setNotes('')
      setConfirmText('')
    }
  }, [isModalOpen])

  const canSubmit =
    !isLoading &&
    Boolean(preview) &&
    !preview?.blocker &&
    Boolean(reason) &&
    confirmText.trim().toUpperCase() === CONFIRM_PHRASE

  const handleSave = () => {
    if (!canSubmit) return
    onSaveClick({ reason, notes }, 'terminate_immediately')
  }

  return (
    <ModalForm
      isModalOpen={isModalOpen}
      onModalClose={closeModal}
      onSaveClick={handleSave}
      modalTitle="Terminate immediately"
      // ModalForm has no `disabled` prop — `loading` is what greys the button
      // out, and buttonLabel keeps it from reading "Saving" while it waits.
      loading={!canSubmit}
      buttonLabel="Terminate Now"
      maxWidth="sm"
    >
      <Box className={classes.dangerBox}>
        <Typography className={classes.dangerHeading}>
          This takes effect today and cannot be undone.
        </Typography>
        <Typography className={classes.infoRow}>
          Sessions later today are cancelled. Sessions that have already run are
          kept. <b>No refund is issued</b> — settle any money owed manually.
        </Typography>
      </Box>

      {preview?.blocker && (
        <Box className={classes.dangerBox}>
          <Typography className={classes.dangerHeading}>
            This enrolment cannot be terminated immediately
          </Typography>
          <Typography className={classes.infoRow}>{preview.blocker}</Typography>
        </Box>
      )}

      {preview && (
        <Box className={classes.infoBox}>
          {preview.affects_other_programs && (
            <Typography className={classes.dangerHeading}>
              This reaches beyond the row you clicked — unsubscribing terminates
              every program on this journey.
            </Typography>
          )}
          <Typography className={classes.infoRow}>
            Effective from:{' '}
            <b>{dayjs(preview.effective_from).format('DD MMM YYYY')}</b>
          </Typography>
          <Typography className={classes.infoRow}>
            Program enrolments terminated:{' '}
            <b>{preview.program_enrolment_count}</b>
          </Typography>
          <Typography className={classes.infoRow}>
            Class enrolments terminated: <b>{preview.enrolment_count}</b>
            {preview.makeup_count > 0
              ? ` (including ${preview.makeup_count} make-up)`
              : ''}
          </Typography>
          {preview.bare_enrolment_count > 0 && (
            <Typography className={classes.infoRow}>
              Extra roster rows swept: <b>{preview.bare_enrolment_count}</b>
            </Typography>
          )}
          <Typography className={classes.infoRow}>
            Upcoming sessions cancelled: <b>{preview.sessions_to_cancel}</b>
          </Typography>
          {(preview.programs || []).map((program) => (
            <Typography key={program.uuid} className={classes.programRow}>
              • {program.program || 'Program'}
              {program.batch ? ` — ${program.batch}` : ''}
              {program.student ? ` — ${program.student}` : ''}
              {program.is_clicked_row ? '' : ' (other program on this journey)'}
            </Typography>
          ))}
        </Box>
      )}

      <FormControl className={classes.formControl} variant="outlined" required>
        <InputLabel shrink>Reason for termination</InputLabel>
        <Select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          label="Reason for termination"
          displayEmpty
        >
          <MenuItem value="" disabled>
            Select a reason
          </MenuItem>
          {reasons.map((item) => (
            <MenuItem key={item} value={item}>
              {item}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        className={classes.formControl}
        label="Notes"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        variant="outlined"
        multiline
        minRows={2}
      />

      <TextField
        className={classes.formControl}
        label={`Type ${CONFIRM_PHRASE} to confirm`}
        value={confirmText}
        onChange={(e) => setConfirmText(e.target.value)}
        variant="outlined"
        required
        InputLabelProps={{ shrink: true }}
      />
    </ModalForm>
  )
}

TerminateImmediatelyForm.propTypes = {
  isModalOpen: PropTypes.bool,
  formData: PropTypes.object,
  onSaveClick: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired
}

export default TerminateImmediatelyForm
